/**
 * The older dialogs' shared pieces, now thin wrappers over the design system
 * (components/ui/ds) so every screen that still imports them follows the
 * theme: `ErrorMessage` is a danger `Notice`, `Button` the ds `Button`, and
 * `LoadingSpinner` a `meta` line. New code imports the ds pieces directly.
 */
import React from 'react';
import { Modal as UIModal, ModalProps as UIModalProps } from '@turn-connect/ui';
import { Button as DsButton, Notice } from '../ui/ds';
import { dashboardStyles } from './dashboardStyles';

// Re-export Modal from @turn-connect/ui for backward compatibility
export const Modal = UIModal;
export type ModalProps = UIModalProps;

/** A one-line "Loading…" in the `meta` role. */
export const LoadingSpinner: React.FC<{ message?: string; text?: string }> = ({ message = 'Loading...', text }) => {
  return (
    <div style={dashboardStyles.loadingSpinner}>
      <span aria-hidden style={{ marginRight: 8 }}>
        ⟳
      </span>
      {text || message}
    </div>
  );
};

/** An error as the ds danger Notice (announced); nothing when there is no message. */
export const ErrorMessage: React.FC<{ message: string | null }> = ({ message }) => {
  if (!message) return null;
  return <Notice tone="danger">{message}</Notice>;
};

interface ButtonProps {
  children: React.ReactNode;
  onClick: (e?: React.MouseEvent) => void;
  variant?: 'primary' | 'secondary' | 'danger';
  size?: 'normal' | 'small';
  disabled?: boolean;
  type?: 'button' | 'submit';
}

/** The ds Button under the older props: `small` is the compact density (44px on a phone). */
export const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  variant = 'primary',
  size = 'normal',
  disabled = false,
  type = 'button',
}) => (
  <DsButton variant={variant} density={size === 'small' ? 'compact' : undefined} onClick={onClick} disabled={disabled} type={type}>
    {children}
  </DsButton>
);
